// @ts-check
import { RETRY_BASE_ATTEMPTS, RETRY_BASE_TIME } from "./Constants";

/**
 * Delays execution for a given number of milliseconds.
 *
 * @param {number} ms - Time to wait in milliseconds
 * @returns {Promise<void>}
 */
export const delayPromise = async (ms) =>
	new Promise(resolve => setTimeout(resolve, ms));

/**
 * @typedef {Object} FetchWithRetryOptions
 * @property {number} attemptsLeft
 * @property {string=} debugName
 * @property {number} initialAttempts
 */

/**
 * 
 * @param {string} url 
 * @param {FetchWithRetryOptions} opts 
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, opts) {
	/** @type {Response} */
	let response;
	try {
		response = await fetch(url);

		if (!response.ok) {
			const errorMessage = `[${opts?.debugName ?? "fetchWithRetry"}] Response status not OK`;
			console.warn(errorMessage, response);
			throw new Error(errorMessage);
		}

		return response;
	} catch (error) {
		if (opts.attemptsLeft - 1 > 0) {
			// retry
			const currentAttempt = opts.initialAttempts + 1 - opts.attemptsLeft;
			await delayForAttempt(currentAttempt);

			opts.attemptsLeft--;

			return fetchWithRetry(url, opts);
		}

		// that was final attempt and it failed
		console.error("Cannot load", url, error);
		throw new Error(`[${opts?.debugName ?? "fetchWithRetry"}] Cannot load resource`);
	}
}

/**
 * @template {object} ResponseData
 * @param {string} url 
 * @param {string | FetchWithRetryOptions} optsOrDebugName 
 * @returns {Promise<ResponseData>}
 */
export async function fetchJsonWithRetry(url, optsOrDebugName = "fetchWithRetry") {
	/** @type {FetchWithRetryOptions} */
	let opts;

	if (typeof optsOrDebugName === "string") {
		opts = {
			initialAttempts: RETRY_BASE_ATTEMPTS,
			attemptsLeft: RETRY_BASE_ATTEMPTS,
			debugName: optsOrDebugName,
		};
	} else {
		opts = optsOrDebugName;
	}

	const response = await fetchWithRetry(url, opts);

	try {
		return await response.json();
	} catch (error) {
		if (opts.attemptsLeft - 1 > 0) {
			// json stream parsing failed
			// retry whole thing
			const currentAttempt = opts.initialAttempts + 1 - opts.attemptsLeft;
			await delayForAttempt(currentAttempt);

			opts.attemptsLeft--;
			return fetchJsonWithRetry(url, opts);
		}

		console.error("Unable to parse", url, error);
		throw new Error(`[${opts?.debugName ?? 'fetchWithRetry'}] Cannot parse JSON`);
	}
}

/**
 * 
 * @param {number} currentAttempt 
 */
export async function delayForAttempt(currentAttempt) {
	// 3, 9 sec by default
	const ms = Math.pow(3, currentAttempt) * RETRY_BASE_TIME;
	await delayPromise(ms);
}
